import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Row, Col, Tabs, Tab } from 'react-bootstrap';
import { SketchPicker } from 'react-color';
import ReactSwitch from 'react-switch';
import homepageSettingsApi from '../../api/homepageSettings';
import { toastError, toastSuccess } from '../../common/UI/Toasts';

const ThemeEditor = ({ show, onHide, sectionKey, sectionName }) => {
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState({});
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#ffffff');
  const [colorTarget, setColorTarget] = useState(null);

  useEffect(() => {
    if (show && sectionKey) {
      fetchTheme();
    }
  }, [show, sectionKey]);

  const fetchTheme = async () => {
    try {
      setLoading(true);
      const response = await homepageSettingsApi.getSettings();
      const sectionTheme = response?.data?.data?.theme?.sections?.[sectionKey] || {};
      setTheme(sectionTheme);
    } catch (error) {
      console.error('Failed to fetch theme:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleThemeChange = (key, value) => {
    setTheme(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const openColorPicker = (property, currentColor) => {
    setColorTarget(property);
    setSelectedColor(currentColor || '#ffffff');
    setShowColorPicker(true);
  };

  const handleColorSelect = () => {
    if (colorTarget) {
      // Convert color object to rgba string if it has alpha, otherwise use hex
      const colorValue = typeof selectedColor === 'object' && selectedColor.rgb
        ? `rgba(${selectedColor.rgb.r}, ${selectedColor.rgb.g}, ${selectedColor.rgb.b}, ${selectedColor.rgb.a})`
        : selectedColor;
      handleThemeChange(colorTarget, colorValue);
    }
    setShowColorPicker(false);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await homepageSettingsApi.updateSectionTheme(sectionKey, theme);
      toastSuccess('Theme updated successfully');
      onHide();
    } catch (error) {
      toastError('Failed to update theme');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset this section\'s theme to defaults?')) {
      setTheme({});
    }
  };

  const ColorButton = ({ label, value, property }) => (
    <div className="mb-3">
      <div className="d-flex align-items-center justify-content-between p-2 border rounded">
        <span>{label}</span>
        <button
          className="btn btn-sm"
          style={{
            backgroundColor: value || '#ffffff',
            width: '60px',
            height: '30px',
            border: '1px solid #dee2e6'
          }}
          onClick={() => openColorPicker(property, value)}
        />
      </div>
      {value && (
        <small className="text-muted d-block mt-1" style={{ fontSize: '0.75rem' }}>
          {value}
        </small>
      )}
    </div>
  );

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fa fa-palette me-2"></i>
            Theme Settings - {sectionName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Tabs defaultActiveKey="background" className="mb-3">
            {/* Background Settings */}
            <Tab eventKey="background" title="Background">
              <div className="p-3">
                <Form.Group className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Label>Use Gradient</Form.Label>
                    <ReactSwitch
                      uncheckedIcon={false}
                      checkedIcon={false}
                      onChange={(checked) => handleThemeChange('useGradient', checked)}
                      checked={theme.useGradient || false}
                      height={24}
                      width={48}
                      onColor="#28a745"
                      offColor="#6c757d"
                    />
                  </div>
                </Form.Group>

                {!theme.useGradient ? (
                  <ColorButton
                    label="Background Color"
                    value={theme.backgroundColor}
                    property="backgroundColor"
                  />
                ) : (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Gradient Type</Form.Label>
                      <Form.Select
                        value={theme.gradientType || 'linear'}
                        onChange={(e) => handleThemeChange('gradientType', e.target.value)}
                      >
                        <option value="linear">Linear</option>
                        <option value="radial">Radial</option>
                      </Form.Select>
                    </Form.Group>

                    <ColorButton
                      label="Gradient Start Color"
                      value={theme.gradientStart}
                      property="gradientStart"
                    />

                    <ColorButton
                      label="Gradient End Color"
                      value={theme.gradientEnd}
                      property="gradientEnd"
                    />

                    {theme.gradientType === 'linear' && (
                      <Form.Group className="mb-3">
                        <Form.Label>Gradient Angle</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g., 180deg, 45deg"
                          value={theme.gradientAngle || '180deg'}
                          onChange={(e) => handleThemeChange('gradientAngle', e.target.value)}
                        />
                        <Form.Text className="text-muted">
                          Enter angle with unit (e.g., 180deg, 45deg, 90deg)
                        </Form.Text>
                      </Form.Group>
                    )}
                  </>
                )}
              </div>
            </Tab>

            {/* Text Colors */}
            <Tab eventKey="text" title="Text Colors">
              <div className="p-3">
                <ColorButton
                  label="Heading Color"
                  value={theme.headingColor}
                  property="headingColor"
                />

                <ColorButton
                  label="Text Color"
                  value={theme.textColor}
                  property="textColor"
                />

                <ColorButton
                  label="Accent Color"
                  value={theme.accentColor}
                  property="accentColor"
                />
              </div>
            </Tab>

            {/* Button Colors */}
            <Tab eventKey="button" title="Buttons">
              <div className="p-3">
                <ColorButton
                  label="Button Background"
                  value={theme.buttonBackground}
                  property="buttonBackground"
                />

                <ColorButton
                  label="Button Text"
                  value={theme.buttonText}
                  property="buttonText"
                />

                <ColorButton
                  label="Button Hover Background"
                  value={theme.buttonHoverBackground}
                  property="buttonHoverBackground"
                />
              </div>
            </Tab>

            {/* Card/Box Colors */}
            <Tab eventKey="cards" title="Cards">
              <div className="p-3">
                <ColorButton
                  label="Card Background"
                  value={theme.cardBackground}
                  property="cardBackground"
                />

                <ColorButton
                  label="Card Border"
                  value={theme.cardBorder}
                  property="cardBorder"
                />

                <Form.Group className="mb-3">
                  <Form.Label>Card Shadow</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., 0 2px 8px rgba(0,0,0,0.1)"
                    value={theme.cardShadow || ''}
                    onChange={(e) => handleThemeChange('cardShadow', e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    CSS box-shadow value
                  </Form.Text>
                </Form.Group>
              </div>
            </Tab>

            {/* Spacing */}
            <Tab eventKey="spacing" title="Spacing">
              <div className="p-3">
                <Form.Group className="mb-3">
                  <Form.Label>Padding Classes</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., py-5, p-4"
                    value={theme.padding || ''}
                    onChange={(e) => handleThemeChange('padding', e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Bootstrap spacing classes
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Margin Classes</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., my-5, m-4"
                    value={theme.margin || ''}
                    onChange={(e) => handleThemeChange('margin', e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Bootstrap spacing classes
                  </Form.Text>
                </Form.Group>
              </div>
            </Tab>

            {/* Custom CSS */}
            <Tab eventKey="custom" title="Custom CSS">
              <div className="p-3">
                <Form.Group>
                  <Form.Label>Custom CSS</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={10}
                    placeholder="Enter custom CSS rules..."
                    value={theme.customCSS || ''}
                    onChange={(e) => handleThemeChange('customCSS', e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Advanced: Add custom CSS to override styles
                  </Form.Text>
                </Form.Group>
              </div>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-danger" onClick={handleReset} disabled={loading}>
            <i className="fa fa-undo me-1"></i>
            Reset to Default
          </Button>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            <i className="fa fa-save me-1"></i>
            {loading ? 'Saving...' : 'Save Theme'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Color Picker Modal */}
      <Modal show={showColorPicker} onHide={() => setShowColorPicker(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Choose Color</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex justify-content-center">
          <SketchPicker
            color={selectedColor}
            disableAlpha={false}
            onChangeComplete={(color) => {
              // Store the full color object so we can access rgba if needed
              setSelectedColor(color);
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowColorPicker(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleColorSelect}>
            Apply Color
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ThemeEditor;
